import {
  EAnimalPetChargeStatusUI,
  EVerificationStatus,
  EVerificationStatusUi,
  TEditingBooleanFieldUi,
  TEditingFieldUi,
  TFail,
  TFailUI,
  TPropertyInfoUI,
  TPropertyManagementInfoUI,
  TSignatureType,
  TStatusChangeInfoDB,
  TStatusChangeInfoUI,
  TStatusInfoWithSourceDB,
  TStatusInfoWithSourceUI,
} from './GeneralTypes';
import { TDocPacketGet, TDocPacketGetUI } from './DocPacket.types';
import {
  EAnimalPaymentStatusDB,
  EAnimalPaymentStatusUI,
  EAnimalStatusDB,
  EAnimalStatusUI,
  EAnimalTypes,
  EAnimalTypesUI,
  TAnimalStatusDetailsDB,
  TAnimalStatusDetailsUI,
  TChargeInformationDB,
  TChargeInformationUI,
} from './Animal.types';

import {
  EAnimalTagDB,
  EAnimalTagUI,
  ETenantTagDB,
  ETenantTagUI,
} from './Tags.types';
import {
  TApplicationHPPacket,
  TApplicationHPPacketUI,
} from './Applications.types';
import { TSAPacketForm, TTrainerInfo } from './AnimalTraining.types';
import { TFile } from './Storage.types';

export type TTenantSearchListDB = {
  id: string;
  name: string;
  profilePicture: string;
  email: string;
};

export type TTenantSearchListUI = {
  label: string;
  value: string;
};

export enum ETenantDetailsStatusDB {
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  RETRACTED = 'RETRACTED',
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  NOTICE = 'NOTICE',
}

export enum ETenantDetailsStatusUI {
  PENDING = 'Pending',
  REJECTED = 'Rejected',
  APPROVED = 'Approved',
  RETRACTED = 'Retracted',
  MOVED_IN = 'Moved in',
  MOVED_OUT = 'Former Resident',
  NOTICE = 'Notice',
}
export enum ETrainingStatus {
  COMPLETE = 'COMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  INCOMPLETE = 'INCOMPLETE',
}
export enum ETrainingStatusUi {
  COMPLETE = 'COMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  INCOMPLETE = 'INCOMPLETE',
}
export enum EPaymentStatus {
  COMPLETE = 'COMPLETE',
  NOT_REQUIRED = 'NOT_REQUIRED',
  INCOMPLETE = 'INCOMPLETE',
}
export enum EPaymentStatusUi {
  COMPLETE = 'COMPLETE',
  NOT_REQUIRED = 'NOT_REQUIRED',
  INCOMPLETE = 'INCOMPLETE',
}

export type TTraining = {
  title: string;
  status: ETrainingStatus;
};

export type TTrainingUi = {
  title: string;
  status: ETrainingStatusUi;
};
export type TProfileDetailsUI = {
  id: string;
  name: TEditingFieldUi;
  email: TEditingFieldUi;
  phone: TEditingFieldUi;
  profilePicture: TEditingFieldUi;
  haveAnimal: TEditingBooleanFieldUi;
  hpDocumentPackets?: TDocPacketGetUI[];
  trainings: TTrainingUi[];
  paymentStatus: EPaymentStatusUi;
};

export type TProfileDetails = {
  name: string;
  phone: string;
  email: string;
  profilePicture: string;
  haveAnimal: boolean;
  userId: string;
  hpDocumentPackets?: TDocPacketGet[];
  trainings: TTraining[];
  paymentStatus: EPaymentStatus;
};

export type TAnimalMetadataUI = {
  present: boolean;
  description: string | null;
} | null;

export type TAnimalMetadata = {
  present: boolean;
  description: string | null;
} | null;

export type TApplicationProfileUI = Omit<
  TProfileDetailsUI,
  'hpDocumentPackets'
> & {
  fails: TFailUI[];
  documentsFails: TFailUI[];
  hpDocumentPackets: TApplicationHPPacketUI[];
  animalMetadata: TAnimalMetadataUI;
};
export type TApplicationProfile = Omit<TProfileDetails, 'hpDocumentPackets'> & {
  fails: TFail;
  hpDocumentPackets: TApplicationHPPacket[];
  specifiedAnimalMetadata: TAnimalMetadata;
};

export type TSaMetadataUpdateUI = {
  animalId: string;
  disabilityType: 'observable' | 'nonObservable';
};
export type TSaMetadataUpdate = {
  animalId: string;
  trainedForObservableDisability: boolean;
};

export type TEsaMetadataUpdateUI = {
  animalId: string;
  hasEsa: 'yes' | 'no';
  description?: string | null;
};
export type TEsaMetadataUpdate = {
  animalId: string;
  hasEsaLetterForAnimal: boolean;
  description?: string | null;
};

export enum EPetStatusDB {
  HAS_ANIMAL = 'HAS_ANIMAL',
  HAS_NOT_ANIMAL = 'HAS_NOT_ANIMAL',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

export enum EPetStatusUI {
  HAS_ANIMAL = 'Yes',
  HAS_NOT_ANIMAL = 'No',
  NOT_SPECIFIED = 'N/A',
}
export enum EResidencyStateDB {
  APPLICANT = 'APPLICANT',
  RESIDENT = 'RESIDENT',
  NOTICE = 'NOTICE',
  NONE = 'UNDEFINED',
  FUTURE = 'FUTURE',
}

export enum EPetQuantityFilterUI {
  ZERO = '0',
  MORE_THAN_ZERO = 'More than 0',
}

export enum EPetQuantityFilterDB {
  ZERO = 'ZERO',
  MORE_THAN_ZERO = 'MORE_THAN_ZERO',
}
export enum EResidencyStateUI {
  APPLICANT = 'Applicant',
  RESIDENT = 'Resident',
  NOTICE = 'Noticed',
  NONE = 'None',
  FUTURE = 'Future Resident',
}
export type TTenantInfoDB = {
  tenantId: string;
  profilePicture: string | null;
  name: string;
};
export type TTenantInfoUI = {
  tenantId: string;
  tenantName: string;
  profilePicture: string | null;
  firstLetters: string;
};

export type TPetInfoDB = {
  petStatus: EPetStatusDB;
  petsCount: number;
};

export type TPetInfoUI = {
  petStatus: EPetStatusUI;
  petsCount: number;
};

export type TTenantDB = {
  tenantId: string;
  profilePicture: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  username: string | null;
  isUsernameEmail: boolean;
  notificationsEnabled: boolean;
  petStatus: EPetStatusDB;
  petsCount: number;
  state: EResidencyStateDB;
  propertyId: string | null;
  propertyName: string | null;
  propertyManagementId: string | null;
  propertyManagementName: string | null;
  propertyManagementLogo: string | null;
  statusChangeInfo: TStatusInfoWithSourceDB | null;
  unitNumber: string | null;
  /**
   * Whether the account is deactivated. The backend masks name, phone, email and
   * username for a deactivated tenant, so anything comparing those values against
   * something the admin typed has to know it is looking at masked data (PP-9951).
   */
  deactivated: boolean;
  deactivationStatusChangeInfo: TTenantDeactivationStatusDB | null;
};
export type TTenantUI = {
  tenantInfo: TTenantInfoUI;
  phone: string | null;
  email: string | null;
  username: string | null;
  notificationsEnabled: boolean;
  isUsernameEmail: boolean;
  residencyState: EResidencyStateUI;
  petInfo: TPetInfoUI;
  propertyInfo: TPropertyInfoUI | string;
  propertyManagementInfo: TPropertyManagementInfoUI | string;
  statusChangeInfo: TStatusInfoWithSourceUI | null;
  unitNumber: string | null;
  deactivated: boolean;
  deactivationStatusChangeInfo: TTenantDeactivationStatusUI | null;
};

export type TTenantAnimalDataDB = {
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  animalId: string;
  isToPetConverted: boolean;
  breed: { value: string } | null;
  id: string;
  name: string;
  specie: { value: string; predefinedId?: string } | null;
  status: EAnimalStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  tags: EAnimalTagDB[];
  type: EAnimalTypes;
  profilePicture: null | string;
  verificationStatus: EVerificationStatus;
  discoveredTagReason: string | null;
  policySignedBy: string | null;
  policyDateSigned: string | null;
  policyId: string | null;
  policyContent: string | null;
  policyName: string | null;
  statusChangeInfo: TStatusChangeInfoDB | null;
  signedDocType: TSignatureType | null;
  paidUntil: string | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
  petRentChargingStatusDetails: TChargeInformationDB | null;
  petFeeChargingStatusDetails: TChargeInformationDB | null;
};

export type TTenantAnimalDataUI = {
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  animalId: string;
  profilePicture?: string;
  isToPetConverted: boolean;
  breed: string;
  id: string;
  name: string;
  specie: string;
  speciePredefinedId?: string;
  status: EAnimalStatusUI;
  splitStatus: TAnimalStatusDetailsUI;
  tags: EAnimalTagUI[];
  type: EAnimalTypesUI;
  verificationStatus: EVerificationStatusUi;
  discoveredTagReason: string | null;
  policySignedBy: string | null;
  policyDateSigned: string | null;
  policyId: string | null;
  policyContent: string | null;
  policyName: string | null;
  statusChangeInfo: TStatusChangeInfoUI | null;
  signedDocType: TSignatureType | null;
  petRentChargingStatusDetails: TChargeInformationUI | null;
  petFeeChargingStatusDetails: TChargeInformationUI | null;
  paidUntil: string;
  paymentStatus: EAnimalPaymentStatusUI | null;
};

export type TResidentDataDB = {
  tenantId: string;
  email: string | null;
  id: string;
  leaseToDate: string;
  moveInDate: string;
  nextLeaseToDate: string | null;
  name: string;
  phone: string | null;
  profilePicture: string | null;
  status: ETenantDetailsStatusDB;
  animals: TTenantAnimalDataDB[];
  hasAnimal: boolean | null;
  roommateHasAnimal: boolean | null;
  tags: ETenantTagDB[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  statusChangeInfo: TStatusChangeInfoDB | null;
  contactId: string | null;
};

export type TResidentDataUI = {
  tenantId: string;
  email: string | null;
  id: string;
  leaseToDate: string;
  moveInDate: string;
  nextLeaseToDate: string | null;
  name: string;
  phone: string | null;
  profilePicture: string | null;
  status: ETenantDetailsStatusUI;
  animals: TTenantAnimalDataUI[];
  hasAnimal: boolean | null;
  roommateHasAnimal: boolean | null;
  tags: ETenantTagUI[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  statusChangeInfo: TStatusInfoWithSourceUI | null;
  contactId: string | null;
};

export type TResidencyDataDB = {
  propertyManagementLogo: string | null;
  propertyManagementName: string;
  propertyName: string;
  roommateHasAnimal: null | boolean;
  hasAnimal: null | boolean;
  unitNumber: string;
  members: TResidentDataDB[];
  shouldTenantSignAddendum: boolean;
  propertyId: string;
  policyState?: string | null;
  propertyManagementId?: string;
  landlordId?: string;
  priceForFirstAnimal: number | null;
  pricesForOtherAnimals: number | null;
  paymentAgreementDate: string | null;
  shouldTenantPayForPet: boolean;
  contactId: string | null;
};

export type TResidencyDataUI = {
  propertyManagementLogo: string | null;
  roommateHasAnimal: null | boolean;
  hasAnimal: null | boolean;
  propertyManagementName: string;
  propertyName: string;
  propertyId: string;
  unitNumber: string;
  roommates: number;
  policyState?: string | null;
  propertyManagementId?: string;
  landlordId?: string;
  [EAnimalTypesUI.PET]: number;
  [EAnimalTypesUI.SA]: number;
  [EAnimalTypesUI.ESA]: number;
  [EAnimalPetChargeStatusUI.SHOULD_NOT_BE_CHARGE]: number;
  [EAnimalPetChargeStatusUI.GRACE_PERIOD]: number;
  [EAnimalPetChargeStatusUI.BEING_CHARGED]: number;
  [EAnimalPetChargeStatusUI.NOT_BEING_CHARGED]: number;
  members: TResidentDataUI[];
  shouldTenantSignAddendum: boolean;
  priceForFirstAnimal: number | null;
  pricesForOtherAnimals: number | null;
  paymentAgreementDate: string;
  shouldTenantPayForPet: boolean;
  contactId: string | null;
};

export type TTenantDeactivationStatusUI = Pick<
  TStatusChangeInfoUI,
  'by' | 'date' | 'reason'
>;
export type TTenantDeactivationStatusDB = Pick<
  TStatusChangeInfoDB,
  'by' | 'date' | 'reason'
>;

export type TTenantDetailsDB = {
  email: string | null;
  id: string;
  name: string;
  notificationsEnabled: boolean;
  phone: string | null;
  residencies: TResidencyDataDB[];
  deactivationStatusChangeInfo: TTenantDeactivationStatusUI | null;
  isUsernameEmail?: boolean;
  username?: string | null;
};

export type TTenantDetailsUI = {
  id: string;
  email: string | null;
  name: string;
  notificationsEnabled: boolean;
  phone: string | null;
  residencies: TResidencyDataUI[];
  deactivationStatusChangeInfo: TTenantDeactivationStatusDB | null;
  isUsernameEmail?: boolean;
  username?: string | null;
};

export type TChangeTenantEmailDB = {
  newEmail: string;
};
export type TChangeTenantEmailUI = {
  tenantId: string;
  value: string;
};

export type TMarkTenantAsKnownDB = {
  tenantResidencyId: string;
};
export type TMarkTenantAsKnownUI = {
  tenantResidencyId: string;
};

export type TAnimalDetailSADocPacketCreateUI = TSAPacketForm & {
  animalId: string;
  tenantId: string;
};

export type TAnimalDetailSADocPacketCreate = {
  trainerInfo: TTrainerInfo;
  animalId: string;
  saLetters?: TFile[];
  trainingEndDate?: string;
  trainingStartDate?: string;
  tenantId: string;
};

// export enum EResidencyStatusUI { // maybe === EResidenciesStatusUI
//   RESIDENT = 'Resident',
//   APPLICANT = 'Applicant',
//   FUTURE_RESIDENT = 'Future Resident',
//   FORMER_RESIDENT = 'Former Resident',
//   DENIED = 'Not Approved',
//   RETRACTED = 'Retracted',
//   NOTICE = 'Noticed',
// }

// export enum EResidencyStatusDB { // maybe === EResidenciesStatusDB
//   RESIDENT = 'RESIDENT',
//   APPLICANT = 'APPLICANT',
//   FUTURE_RESIDENT = 'FUTURE_RESIDENT',
//   FORMER_RESIDENT = 'FORMER_RESIDENT',
//   DENIED = 'DENIED',
//   RETRACTED = 'RETRACTED',
//   NOTICE = 'NOTICE',
// }

export type TTransferAnimalOptionsUI = {
  animalId: string;
  isTransferable: boolean;
  splitStatus: TAnimalStatusDetailsUI;
  verificationStatus: EVerificationStatusUi[];
};
export type TTransferAnimalOptionsDB = {
  animalStatuses: {
    animalId: string;
    transferable: boolean;
    statusDetails: TAnimalStatusDetailsDB | null;
    verificationStatus: EVerificationStatus[];
  }[];
};

export type TTransferAnimalBodyUI = {
  targetTenantResidencyId: string;
  sourceTenantResidencyId: string;
  animals: {
    animalId: string;
    verificationStatus: EVerificationStatusUi;
  }[];
  // the admin confirmed what the transfer retracts, moves out or merges (PP-10053)
  recordEndingsConfirmed?: boolean;
  // set by a merge: the two accounts belong to one resident, so a paid pet profile fee may
  // follow the pet onto the kept account
  sameResident?: boolean;
};

export type TTransferAnimalBodyDB = {
  targetTenantResidencyId: string;
  sourceTenantResidencyId: string;
  animals: {
    animalId: string;
    verificationStatus: EVerificationStatus;
  }[];
  recordEndingsConfirmed: boolean;
  sameResident: boolean;
};

export type TAnimalTransferPreviewResidencyDB = {
  tenantResidencyId: string;
  propertyName: string | null;
  unitNumber: string | null;
  status: string;
};

export type TAnimalTransferPreviewRecordDB = {
  type:
    | 'ANIMAL_RESIDENCY'
    | 'APPLICATION'
    | 'DISCOVERED_TAG'
    | 'KNOWN_TAG'
    | 'PNC_SCHEDULE';
  // an animal residency's or application's status; null for a tag or PNC schedule
  status: string | null;
  // the charge a PNC schedule is for; null for every other record
  chargeType: 'PET_RENT' | 'PET_FEE' | null;
  from: TAnimalTransferPreviewResidencyDB;
  to: TAnimalTransferPreviewResidencyDB | null;
  action: 'TERMINATE' | 'RETRACT' | 'REMOVE' | null;
};

export type TAnimalTransferPreviewCarriedItemDB =
  | 'VERIFICATION'
  | 'PHOTOS'
  | 'PROFILE_PICTURE'
  | 'VET_RECORDS'
  | 'MICROCHIP'
  | 'PET_FEE';

// Where a retired record's paid pet profile fee lands: the survivor's residency and the date it is paid through.
export type TAnimalTransferPreviewCarriedFeeDB = {
  to: TAnimalTransferPreviewResidencyDB;
  paidThrough: string | null;
};

export type TAnimalTransferPreviewDB = {
  animals: {
    animalId: string;
    animalName: string | null;
    kind: 'TRANSFER' | 'CONSOLIDATE';
    survivorAnimalId: string | null;
    moves: TAnimalTransferPreviewRecordDB[];
    endings: TAnimalTransferPreviewRecordDB[];
    newApplications: TAnimalTransferPreviewResidencyDB[];
    carried: TAnimalTransferPreviewCarriedItemDB[];
    // set when carried holds PET_FEE
    fee: TAnimalTransferPreviewCarriedFeeDB | null;
    blockers: string[];
  }[];
  confirmationRequired: boolean;
  blocked: boolean;
};

// Each animal's outcome as sentences an admin can review before confirming.
export type TAnimalTransferPreviewUI = {
  animals: {
    animalId: string;
    animalName: string;
    isConsolidation: boolean;
    endings: string[];
    newApplications: string[];
    carried: string[];
    blockers: string[];
  }[];
  confirmationRequired: boolean;
  blocked: boolean;
};

export type TTenantMaskedDataDB = {
  email: string | null;
  id: string;
  name: string;
  phone: string | null;
  username: string;
};

export type TTenantMaskedDataUI = {
  id: string;
  email: string | null;
  name: string;
  phone: string | null;
  username: string;
};

export enum EFollowUpMedium {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

export type TFollowUpItemDB = {
  id: string | null;
  author: {
    name: string;
    id: string;
  } | null;
  isEditable: boolean;
  animals:
    | {
        animalId: string;
        name: string;
        species: string | null;
        speciesPredefinedId?: string;
        isDeleted?: boolean;
      }[]
    | null;
  description: string;
  medium: EFollowUpMedium | null;
  occurredOn: string;
};

export type TFollowUpItemUI = {
  id: string | null;
  author: {
    name: string;
    id: string;
  } | null;
  isEditable: boolean;
  animals: {
    animalId: string;
    name: string;
    species: string | null;
    speciesPredefinedId?: string;
    isDeleted?: boolean;
  }[];
  description: string;
  medium: EFollowUpMedium | null;
  occurredOn: string;
};

export type TAddTenantFollowUpItemUI = {
  description: string;
  animalIds?: string[];
  tenantId: string;
};
export type TAddTenantFollowUpItemDB = {
  description: string;
  animalIds: string[] | null;
  tenantId: string;
};

export type TEditTenantFollowUpItemUI = {
  tenantId: string;
  tenantFollowUpItemId: string;
  initialAnimals: string[];
  finalAnimals: string[];
  description: string;
};
export type TEditTenantFollowUpItemDB = {
  tenantId: string;
  tenantFollowUpItemId: string;
  addedAnimals: string[] | null;
  removedAnimals: string[] | null;
  description: string;
};

export type TTenantAnimals = {
  animalId: string;
  animalName: string;
};

export type TMergedFieldSource = 'SOURCE' | 'DESTINATION';

export type TMergeTenantsUI = {
  sourceTenantId: string;
  destinationTenantId: string;
  username: TMergedFieldSource;
  email: TMergedFieldSource;
  phone: TMergedFieldSource;
};

export type TSplitLoginMoveKind = 'PARENT_LOGIN' | 'CHILD_LOGIN' | 'NEW_LOGIN';

export type TSplitNewProfileUI = {
  name: string;
  email?: string;
  phone?: string;
};

export type TSplitTenantUI = {
  sourceTenantId: string;
  residencyIds: string[];
  destinationTenantId?: string;
  newProfile?: TSplitNewProfileUI;
  movedLogin?: string;
};

export type TSplitTenantDB = {
  sourceTenantId: string;
  residencyIds: string[];
  destinationTenantId: string | null;
  newProfile: {
    name: string;
    email: string | null;
    phone: string | null;
  } | null;
  movedLogin: string | null;
};

// The backend's shape is already what the UI needs, so one type serves both sides.
export type TSplitLogin = {
  username: string;
  current: boolean;
  parentLogin: boolean;
  signsInHere: boolean;
};

export type TSplitHistoryItemDB = {
  splitId: string;
  fromTenantId: string;
  toTenantId: string;
  residencyIds: string[];
  createdAt: string | null;
  loginMoveKind: TSplitLoginMoveKind | null;
  movedUsername: string | null;
  reversal: boolean;
  undone: boolean;
};

export type TSplitHistoryItemUI = Omit<TSplitHistoryItemDB, 'createdAt'> & {
  createdAt: string;
  canUndo: boolean;
};

export type TTenantSplitOptionsDB = {
  logins: TSplitLogin[];
  unsplittableResidencyIds: string[];
  splits: TSplitHistoryItemDB[];
};

export type TTenantSplitOptionsUI = {
  logins: TSplitLogin[];
  // Residencies the backend would refuse to split: animals, or any animal application, deleted ones included.
  unsplittableResidencyIds: string[];
  splits: TSplitHistoryItemUI[];
};
